import React from 'react'
import type { DialogProps as MuiDialogProps } from '@material-ui/core/Dialog'
import MuiDialog from '@material-ui/core/Dialog'
import type { Theme } from '@material-ui/core/styles'
import { createStyles, withStyles, useTheme, makeStyles } from '@material-ui/core/styles'
import { useMediaQuery } from '@material-ui/core'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
// Leaf import, not the providers barrel: this component sits low in the UI package and the
// barrel pulls in far more than the one context it needs.
import { usePdfOwnerDocument } from '../../providers/PdfOwnerDocumentProvider/PdfOwnerDocumentProvider'

type CoreStyleClasses<T extends (...args: any) => any> = Partial<Record<keyof ReturnType<T>, string>>

const coreStyles = (theme: Theme) => {
  const { pxToRem } = theme.typography

  // stub names are to keep typescript happy about existing MuiDialog class names
  return createStyles({
    root: {},
    scrollPaper: {
      // move up dialog location when on > md screens
      [theme.breakpoints.up('md')]: {
        alignItems: 'start'
      }
    },
    scrollBody: {},
    container: {
      minWidth: '100%'
    },
    paper: {
      // fix safari not showing cursor on inner InputFields on nextjs
      zIndex: theme.zIndex.modal,

      maxHeight: '100vh',
      '@media (min-width: 728px)': {
        minWidth: pxToRem(728),
        maxHeight: 'calc(100vh - 96px)'
      },
      '@media (max-width: 727px)': {
        width: '100%'
      }
    },
    paperScrollPaper: {},
    paperScrollBody: {},
    paperWidthXs: {},
    paperWidthSm: {},
    paperWidthMd: {},
    paperWidthLg: {},
    paperFullWidth: {},
    paperFullScreen: {
      borderRadius: '0 !important',
      maxHeight: '100vh !important'
    }
  })
}

const useStyles = makeStyles<Theme, DialogProps>({
  root: {},
  paper: {},
  paperFullScreen: {}
})

export interface DialogProps extends Omit<MuiDialogProps, 'title' | 'classes' | 'ref'> {
  classes?: CoreStyleClasses<typeof coreStyles> & StyleClasses<typeof useStyles>
  children?: React.ReactNode
  forceFullScreen?: boolean
  loading?: boolean
}

const CoreDialog = withStyles(coreStyles)((props: DialogProps) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const { forceFullScreen, children, container, ...otherProps } = props
  /*
   * A dialog opened from inside a react-frame-component iframe (the v2 presentation preview) renders
   * in the iframe's React tree — so its JSS classes are injected into the iframe's head — but MUI's
   * Modal portals the DOM to `globalThis.document.body`, the page *behind* the iframe. The dialog
   * then mounts where none of its styles exist: unstyled, unpositioned and invisible, while the
   * backdrop (not portalled) dims the iframe correctly. It reads as "the click did nothing"
   * (ENG-1624). Portalling into the iframe's own body puts the DOM back with its styles.
   *
   * The context is named for pdf.js, which needed the same document for the same reason, and
   * `PresentationImbedPreview` already wraps its DialogStackProvider in it precisely so dialogs can
   * find the iframe. Undefined everywhere else, which leaves MUI's own default in place.
   */
  const frameDocument = usePdfOwnerDocument()

  return (
    <MuiDialog
      fullScreen={forceFullScreen || fullScreen}
      scroll="paper"
      {...(otherProps as MuiDialogProps)}
      container={container ?? frameDocument?.body}
      onClose={(ev, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return false
        }

        props.onClose?.(ev, reason)
      }}
    >
      {children}
    </MuiDialog>
  )
})

function Dialog(props: DialogProps) {
  const classes = useStyles(props)

  return (
    <CoreDialog {...props} classes={{ ...classes, root: classNames(classes.root, 'tour-hide-checklist-launcher') }}>
      {props.children}
    </CoreDialog>
  )
}

export default Dialog
